import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../App.css'; // Assuming a CSS file for styling
import { SignedIn, SignedOut, useClerk, useUser, useAuth } from '@clerk/clerk-react';

const API_URL = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '')) + '/api';

function AdminAppointments() {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { getToken } = useAuth();
  // State to track the list of all appointments
  const [appointments, setAppointments] = useState([]);
  // ... rest of state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editFormData, setEditFormData] = useState({
    tijd: '',
    rijschool: ''
  });
  const [drivingSchools, setDrivingSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [adminRijschool, setAdminRijschool] = useState("");

  const fetchAdminRijschool = async () => {
    if (!user) return;
    try {
      const token = await getToken();
      const response = await axios.get(`${API_URL}/get-user-status/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.rijschool_naam) {
        setAdminRijschool(response.data.rijschool_naam);
      }
    } catch (error) {
      console.error('Error fetching admin rijschool:', error);
    }
  };

  const fetchAppointmentsForDate = async (date, schoolName) => {
    if (!date || isNaN(date.getTime())) return;
    
    const currentSchool = schoolName || adminRijschool;
    console.log("Fetching appointments for:", currentSchool, "on date:", date);
    if (!currentSchool) {
      console.warn("No school name provided for fetch!");
      return;
    }

    setIsLoading(true);
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      let url = `${API_URL}/appointments/${formattedDate}`;
      // Note: rijschool query param is now mostly for logging as server uses token to identify rijschool
      url += `?rijschool=${encodeURIComponent(currentSchool)}`;
      console.log("Request URL:", url);

      const token = await getToken();
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Fetched appointments:", response.data);
      setAppointments(Array.isArray(response.data) ? response.data : []);
      setMessage({ text: '', type: '' });
    } catch (error) {
      console.error('Error fetching appointments:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error.';
      setMessage({ text: `Error fetching appointments: ${errorMessage}`, type: 'error' });
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDrivingSchools = async () => {
    try {
      const response = await axios.get(`${API_URL}/rijscholen`);
      setDrivingSchools(response.data);
    } catch (error) {
      console.error('Error fetching driving schools:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (user) {
        let school = adminRijschool;
        if (!school) {
          // Fetch school name if we don't have it yet
          try {
            const token = await getToken();
            const response = await axios.get(`${API_URL}/get-user-status/${user.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.rijschool_naam) {
              school = response.data.rijschool_naam;
              setAdminRijschool(school);
            }
          } catch (e) { console.error(e); }
        }
        fetchAppointmentsForDate(selectedDate, school);
      }
    };
    init();
    fetchDrivingSchools();
  }, [selectedDate, user]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setEditingAppointment(null);
  };

  const handleDelete = async (id) => {
    console.log("handleDelete called for ID:", id);
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await axios.delete(`${API_URL}/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Delete response:", response.data);
      setMessage({ text: 'Appointment successfully deleted.', type: 'success' });
      console.log("Refreshing list for:", adminRijschool);
      fetchAppointmentsForDate(selectedDate, adminRijschool);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error.';
      setMessage({ text: `Error deleting appointment: ${errorMessage}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (appointment) => {
    setEditingAppointment(appointment.id);
    setEditFormData({
      tijd: appointment.tijd,
      rijschool: appointment.rijschool
    });
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleEditFormSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/appointments/${editingAppointment}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: 'Appointment successfully edited.', type: 'success' });
      setEditingAppointment(null);
      fetchAppointmentsForDate(selectedDate, adminRijschool);
    } catch (error) {
      console.error('Error editing appointment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error.';
      setMessage({ text: `Error editing appointment: ${errorMessage}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminRijschool');
    setAdminRijschool("");
    navigate('/');
  };

  const displayRijschool = adminRijschool || "Laden...";

  return (
    <>
      <SignedIn>
        <div className="main-content-wrapper fade-in-up" style={{ maxWidth: '1200px', marginTop: '40px' }}>
          <div className="top-left-fixed" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={() => navigate('/')} className="action-button-blue">Home</button>
            <button onClick={() => navigate('/admin/login')} className="action-button-blue">Terug</button>
          </div>
          <div className="top-right-fixed">
            <button onClick={handleLogout} className="action-button-red">Uitloggen</button>
          </div>
          
          <div style={{ marginBottom: '40px' }}>
            <h1 className="aharoni-style" style={{ fontSize: '2.5em', marginBottom: '5px' }}>
              Afspraken Beheer
            </h1>
            <p style={{ color: '#007bff', fontSize: '1.2em', fontWeight: '600' }}>{displayRijschool}</p>
          </div>

          {message.text && (
            <div className={`message-box ${message.type}`} style={{ 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px', 
              width: '100%',
              maxWidth: '800px',
              backgroundColor: message.type === 'success' ? '#28a74522' : '#dc354522',
              border: `1px solid ${message.type === 'success' ? '#28a745' : '#dc3545'}`,
              color: 'white'
            }}>
              {message.text}
            </div>
          )}

          <div className="admin-grid" style={{ width: '100%', alignItems: 'start' }}>
            <div className="admin-card" style={{ padding: '20px', height: 'fit-content' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.5em' }}>Selecteer Datum</h3>
              <div className="admincalender">
                <Calendar
                  onChange={handleDateChange}
                  value={selectedDate}
                  locale="nl-NL"
                />
              </div>
            </div>

            <div className="admin-card" style={{ textAlign: 'left', alignItems: 'stretch', padding: '30px' }}>
              <h2 style={{ marginBottom: '25px', color: '#007bff', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                Afspraken op {selectedDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
              </h2>
              
              {isLoading ? (
                <p>Laden...</p>
              ) : appointments.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {appointments.map((appointment) => (
                    <li key={appointment.id} className="fade-in-up" style={{ 
                      backgroundColor: '#1e1e1e', 
                      border: '1px solid #333',
                      borderRadius: '10px',
                      padding: '20px',
                      marginBottom: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '15px'
                    }}>
                      {editingAppointment === appointment.id ? (
                        <form onSubmit={handleEditFormSubmit} style={{ width: '100%' }}>
                          <div style={{ marginBottom: '15px' }}>
                            <p style={{ margin: '0 0 5px 0' }}><strong>Leerling:</strong> {appointment.leerling || 'Onbekend'}</p>
                            <p style={{ margin: 0 }}><strong>Datum:</strong> {new Date(appointment.datum + 'T00:00:00').toLocaleDateString('nl-NL')}</p>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <label className="form-label" style={{ marginBottom: '5px' }}>Tijd:</label>
                            <input
                              type="text"
                              name="tijd"
                              value={editFormData.tijd || ''}
                              onChange={handleEditFormChange}
                              className="form-input"
                              style={{ marginBottom: 0 }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="submit" className="btn" style={{ minWidth: '100px', flex: 1 }} disabled={isLoading}>Opslaan</button>
                            <button onClick={() => setEditingAppointment(null)} className="btn btn-danger" style={{ minWidth: '100px', flex: 1 }} disabled={isLoading}>Annuleren</button>
                          </div>
                        </form>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            <p style={{ fontSize: '1.25em', margin: '0 0 5px 0', color: '#fff' }}><strong>{appointment.leerling || 'Onbekend'}</strong></p>
                            <p style={{ color: '#aaa', margin: '0', fontSize: '1.1em' }}>Tijd: <span style={{ color: '#007bff', fontWeight: 'bold' }}>{appointment.tijd || 'Onbekend'}</span></p>
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => handleEditClick(appointment)} className="btn" style={{ minWidth: '100px', padding: '10px 20px', margin: 0 }} disabled={isLoading}>Bewerken</button>
                            <button onClick={() => handleDelete(appointment.id)} className="btn btn-danger" style={{ minWidth: '100px', padding: '10px 20px', margin: 0 }} disabled={isLoading}>Verwijderen</button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
                  <p style={{ fontSize: '1.2em', fontStyle: 'italic' }}>Geen afspraken gevonden op deze datum.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </SignedIn>
      <SignedOut>
        <Navigate to="/admin/login" />
      </SignedOut>
    </>
  );
}

export default AdminAppointments;
